<script setup lang="ts">
import { CompanyCard, CompanyDetailsTabs } from '@/features/companies';
import {
  MasterBlogPostsBlock,
  MasterCard,
  MasterDetailsTabs,
  MasterHeaderPreview,
} from '@/features/masters';
import type { PluralPartnerType } from '../types';
import { partnerTypeFromPlural } from '@glow/shared/features/partners';
import PartnerWorkPlaceSelectBlock from '../components/PartnerWorkPlaceSelectBlock.vue';
import PartnerContextMenu, {
  type PartnerContextMenuReportData,
} from '../components/PartnerContextMenu.vue';
import type { AppBreadcrumbsItem } from '@glow/shared/types/ui/AppBreadcrumbs';
import { graphql } from '@/gql';
import type { RouteLocationRaw } from 'vue-router';
import { useRoutes } from '@glow/shared/lib/routes';
import { useWindowScroll } from '@vueuse/core';
import { useQuery } from '@urql/vue';
import { CartMasterBlock } from '@/features/cart';
import { AppointmentTimepicker } from '@/features/appointments';

const { getUrl } = useRoutes();
const route = useRoute();
const partnerType = computed(() =>
  partnerTypeFromPlural(route.params.type as PluralPartnerType)
);
const partnerSlug = computed(() => route.params.partner as string);

const { data: masterQueryData } = await useQuery({
  query: graphql(/* GraphQL */ `
    query Details_MasterQuery($slug: String!) {
      master(slug: $slug) {
        id
        fullName

        ...MasterCard_MasterFragment
        ...MasterDetailsTabs_MasterFragment
        ...MasterHeaderPreview_MasterFragment
      }
    }
  `),
  variables: computed(() => ({ slug: partnerSlug.value })),
  pause: toRef(() => partnerType.value !== 'master'),
});
const master = toRef(() => masterQueryData.value?.master);
const { data: companyQueryData } = await useQuery({
  query: graphql(/* GraphQL */ `
    query Details_CompanyQuery($slug: String!) {
      company(slug: $slug) {
        id
        name

        ...CompanyCard_CompanyFragment
        ...CompanyDetailsTabs_CompanyFragment
      }
    }
  `),
  variables: computed(() => ({ slug: partnerSlug.value })),
  pause: toRef(() => partnerType.value !== 'company'),
});
const company = toRef(() => companyQueryData.value?.company);

const partnerName = computed(
  () =>
    (partnerType.value === 'master'
      ? master.value?.fullName
      : company.value?.name) ?? ''
);
const partnerId = computed(() =>
  partnerType.value === 'master' ? master.value?.id : company.value?.id
);

const { showReportedToast } = useContextToasts();
const isContextMenuVisible = ref(false);
const locationIndex = ref<number | undefined>(0);

// useSeoMeta({
//   description: () =>
//     partnerType.value === 'master'
//       ? masterData.value?.meta.description
//       : companyData.value?.meta.description,
// });
// useHttpCache(
//   computed(() =>
//     partnerType.value === 'master'
//       ? masterData.value?.updatedAt
//       : companyData.value?.updatedAt
//   )
// );

const addressIndex = ref(0);
provide('partnerAddressIndex', addressIndex);

const { clientBaseUrl } = useRuntimeConfig().public;
const router = useRouter();
useHead({
  title: partnerName,
  link: computed(() => {
    const canonicalPath = router.resolve({
      name: 'Partner details',
      params: route.params,
    }).href;
    const canonicalHref = new URL(canonicalPath, clientBaseUrl).toString();
    return [{ rel: 'canonical', href: canonicalHref }];
  }),
});

const catalogLink: RouteLocationRaw = {
  name: 'Partner catalog',
  params: {
    type: route.params.type,
    city: route.params.city,
  },
};
const breadcrumbs = computed<AppBreadcrumbsItem[]>(() => [
  {
    title: 'Главная',
    link: getUrl('clientHome'),
  },
  {
    // title: masterData.value?.meta.catalogTitle ?? 'Каталог',
    title: 'Каталог',
    link: catalogLink,
  },
  { title: partnerName.value },
]);

const { executeMutation: reportMaster } = useMutation(
  graphql(/* GraphQL */ `
    mutation Details_ReportMasterMutation($id: Int!, $input: ReportInput!) {
      reportMaster(id: $id, input: $input) {
        id
      }
    }
  `)
);
const { executeMutation: reportCompany } = useMutation(
  graphql(/* GraphQL */ `
    mutation Details_ReportCompanyMutation($id: Int!, $input: ReportInput!) {
      reportCompany(id: $id, input: $input) {
        id
      }
    }
  `)
);

async function onReport(input: PartnerContextMenuReportData) {
  if (!partnerId.value) return;
  const reportFunction =
    partnerType.value === 'master' ? reportMaster : reportCompany;
  await reportFunction({ id: partnerId.value, input });
  showReportedToast();
}

const { y: scrollY } = useWindowScroll();
</script>

<template>
  <div>
    <NuxtLayout
      name="app"
      :title="partnerName"
      :with-gutter="false"
      @show-context="isContextMenuVisible = true"
    >
      <template #header-start>
        <div class="fs-1 fw-500">Мастер</div>
      </template>
      <template #sidebar>
        <CartMasterBlock
          v-if="partnerType === 'master' && partnerId"
          :master-id="partnerId"
        />
      </template>
      <template #grid-prepend>
        <AppBreadcrumbs class="d-none d-md-flex mb-6" :items="breadcrumbs" />
      </template>

      <div class="details">
        <div class="details-container">
          <div class="page-card p-4 p-sm-6">
            <MasterCard
              v-if="partnerType === 'master' && master"
              v-model:location-index="locationIndex"
              :master="master"
            />
            <CompanyCard
              v-else-if="partnerType === 'company' && company"
              :company="company"
            />
          </div>
        </div>

        <MasterBlogPostsBlock
          v-if="partnerType === 'master' && partnerId"
          :master-id="partnerId"
        />

        <div id="work-places" class="details-container">
          <PartnerWorkPlaceSelectBlock
            v-if="partnerId"
            :partner-type="partnerType"
            :partner-id="partnerId"
            @select="(index) => (addressIndex = index)"
          />
        </div>

        <AppointmentTimepicker class="page-card" :appointable-periods="[]" />

        <div class="details__tabs">
          <MasterDetailsTabs
            v-if="partnerType === 'master' && master"
            :master="master"
            :location-index="locationIndex"
          />
          <CompanyDetailsTabs
            v-else-if="partnerType === 'company' && company"
            :company="company"
          />
        </div>
      </div>

      <div
        class="details__header"
        :class="{ details__header_hidden: scrollY < 350 }"
      >
        <div class="container py-2 px-0">
          <MasterHeaderPreview
            v-if="partnerType === 'master' && master"
            :master="master"
          />
        </div>
      </div>
      <PartnerContextMenu
        v-if="partnerId"
        v-model="isContextMenuVisible"
        :partner-type="partnerType"
        :partner-id="partnerId"
        :block-redirect-link="catalogLink"
        @report="onReport"
      />
    </NuxtLayout>
  </div>
</template>

<style lang="scss" scoped>
@import '@glow/shared/assets/core.scss';

.details {
  display: flex;
  flex-direction: column;
  gap: var(--app-spacing-6);

  &__map {
    position: relative;
    width: 100%;
    height: 340px;
    overflow: hidden;
    z-index: 5;

    @include media-breakpoint-down(sm) {
      height: 315px;
      border-radius: 0;
    }
  }

  &__header {
    position: fixed;
    top: var(--layout-fixed-padding-top);
    left: 0;
    width: 100%;
    height: var(--layout-details-header-height);
    transition: var(--app-transition-md);
    background: var(--app-color-white);
    z-index: 100;
    box-shadow: 0px 0px 16px 0px rgba(20, 20, 20, 0.16);
    clip-path: inset(0px -16px -16px -16px);

    &_hidden {
      z-index: 99;
      visibility: hidden;
      transform: translateY(-100%);
    }
  }
}
</style>

<style lang="scss">
.app-layout {
  --layout-details-header-height: 60px;

  &:not(:has(.details__header_hidden)) {
    --layout-auth-popover-offset: var(
      --layout-details-header-height
    ) !important;
  }
}

.details__tabs {
  .tabs__content {
    padding: 0;
  }

  .tabs__header,
  .service-group__header {
    @include media-breakpoint-down(sm) {
      padding: 0 var(--app-spacing-4);
    }
  }

  .slider {
    padding: 0 var(--app-spacing-6);

    @include media-breakpoint-down(sm) {
      padding: 0 var(--app-spacing-4);
    }

    &__prev {
      left: var(--app-spacing-4);
    }

    &__next {
      right: var(--app-spacing-4);
    }
  }
}
</style>
